import { useState } from 'react';
import { useQuery } from 'react-query';
import { MentorshipReach as Reach, MentorshipSeason, ReachColumn, fetchMentorshipReach } from '../services/api';
import { Panel, PanelHeader, PanelBody } from './ui/Panel';
import { LoadingPanel, ErrorNotice, EmptyState } from './ui/States';
import { fmt, DASH } from '../lib/format';

const headCell = 'font-semibold text-small text-ink-2 py-[10px]';
const cell = 'py-[10px] text-table text-right tabular';

const rate = (n: number | null) => (n === null ? DASH : `${n}%`);

const DIMENSION_LABEL: Record<string, string> = {
  genderIdentity: 'Gender identity',
  geographicRegion: 'Region',
  careerSeniority: 'Career stage',
  discipline: 'Discipline',
  sector: 'Sector',
};

const COLUMNS: { key: ReachColumn; label: string }[] = [
  { key: 'members', label: 'Members' },
  { key: 'mentees', label: 'Mentees' },
  { key: 'paired', label: 'Paired' },
  { key: 'attended', label: 'Attended' },
  { key: 'mentors', label: 'Mentors' },
];

export default function MentorshipReach({ seasons }: { seasons: MentorshipSeason[] }) {
  const [dimension, setDimension] = useState('isRacialized');
  const [season, setSeason] = useState<string>(seasons[0] ? String(seasons[0].seasonId) : '');
  const seasonId = season ? Number(season) : undefined;

  const reach = useQuery<Reach>(['mentorship-reach', dimension, seasonId], () =>
    fetchMentorshipReach(dimension, seasonId),
  );
  const data = reach.data;

  return (
    <Panel className="mt-6">
      <PanelHeader title="Mentorship reach" note="By the platform's own demographics">
        <div className="flex items-center gap-2">
          <select
            aria-label="Dimension"
            value={dimension}
            onChange={(e) => setDimension(e.target.value)}
            className="border border-line-strong bg-white px-2 py-[6px] text-small"
          >
            {(data?.dimensions ?? [{ key: 'isRacialized', label: 'Racialized' }]).map((d) => (
              <option key={d.key} value={d.key}>
                {DIMENSION_LABEL[d.key] ?? d.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Season"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="border border-line-strong bg-white px-2 py-[6px] text-small"
          >
            <option value="">All seasons</option>
            {seasons.map((s) => (
              <option key={s.seasonId} value={s.seasonId}>
                {s.seasonName}
              </option>
            ))}
          </select>
        </div>
      </PanelHeader>
      {reach.isLoading ? (
        <LoadingPanel label="Loading reach…" height={200} />
      ) : reach.error || !data ? (
        <PanelBody>
          <ErrorNotice>Could not load mentorship reach. Retry in a moment.</ErrorNotice>
        </PanelBody>
      ) : data.categories.length === 0 ? (
        <EmptyState title="No answers for this dimension">
          Members have not answered this question, so reach cannot be broken down by it.
        </EmptyState>
      ) : (
        <PanelBody>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-line">
                  <th className={`${headCell} text-left`}>{DIMENSION_LABEL[data.dimension] ?? data.label}</th>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={`${headCell} text-right`}>
                      {c.label}
                    </th>
                  ))}
                  <th className={`${headCell} text-right`}>Participation</th>
                  <th className={`${headCell} text-right`}>Pairing</th>
                  <th className={`${headCell} text-right`}>Attendance</th>
                </tr>
              </thead>
              <tbody>
                {data.categories.map((c) => (
                  <tr key={c.value} className="border-b border-line">
                    <td className="py-[10px] text-table">{c.value}</td>
                    {COLUMNS.map((col) => (
                      <td key={col.key} className={cell}>
                        {fmt(c[col.key])}
                      </td>
                    ))}
                    <td className={`${cell} text-ink-2`}>{rate(c.participationRate)}</td>
                    <td className={`${cell} text-ink-2`}>{rate(c.pairingRate)}</td>
                    <td className={`${cell} text-ink-2`}>{rate(c.attendanceRate)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="py-[10px] text-table font-semibold">All answered</td>
                  {COLUMNS.map((col) => (
                    <td key={col.key} className={`${cell} font-semibold`}>
                      {fmt(data.totals[col.key])}
                    </td>
                  ))}
                  <td className={`${cell} font-semibold`}>{rate(data.totals.participationRate)}</td>
                  <td className={`${cell} font-semibold`}>{rate(data.totals.pairingRate)}</td>
                  <td className={`${cell} font-semibold`}>{rate(data.totals.attendanceRate)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-small text-ink-2 mt-4 pt-3 border-t border-line">
            Participation is active mentees as a share of members in the group; pairing is paired mentees as a share of
            mentees; attendance is paired mentees with at least one attended session. Staff, partner organizations and
            archived members are excluded; mentees an app admin removed before matching are not counted.
          </p>
        </PanelBody>
      )}
    </Panel>
  );
}
